import { create } from 'zustand';
import { Project } from '../types';
import { projectsApi } from '../services/api';
import { useWorkspaceStore } from './workspaceStore';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;
  fetchProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  createProject: (data: Partial<Project>, memberIds?: string[]) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>, addedMemberIds?: string[], removedMemberIds?: string[]) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  loading: false,
  error: null,

  fetchProjects: async () => {
    set({ loading: true, error: null });
    try {
      const response = await projectsApi.getAll(useWorkspaceStore.getState().activeWorkspaceId || undefined);
      set({ projects: response.data, loading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch projects';
      set({ error: message, loading: false });
    }
  },

  fetchProject: async (id) => {
    set({ loading: true });
    try {
      const response = await projectsApi.getById(id);
      set({ currentProject: response.data, loading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch project';
      set({ error: message, loading: false });
    }
  },

  createProject: async (data, memberIds) => {
    const response = await projectsApi.create({
      ...data,
      workspace_id: data.workspace_id || useWorkspaceStore.getState().activeWorkspaceId,
    });
    const newProject = response.data;
    if (memberIds?.length) {
      await Promise.all(memberIds.map(userId => projectsApi.addMember(newProject.id, userId, 'editor')));
    }
    set({ projects: [newProject, ...get().projects] });
    return newProject;
  },

  updateProject: async (id, data, addedMemberIds, removedMemberIds) => {
    const response = await projectsApi.update(id, data);
    const updated = response.data;
    if (addedMemberIds?.length) {
      await Promise.all(addedMemberIds.map(userId => projectsApi.addMember(id, userId, 'editor')));
    }
    if (removedMemberIds?.length) {
      await Promise.all(removedMemberIds.map(userId => projectsApi.removeMember(id, userId)));
    }
    set({
      projects: get().projects.map(p => p.id === id ? updated : p),
      currentProject: get().currentProject?.id === id ? updated : get().currentProject,
    });
  },

  deleteProject: async (id) => {
    await projectsApi.delete(id);
    set({ projects: get().projects.filter(p => p.id !== id) });
  },
}));
